import { useState, useEffect, useRef } from 'react'
import { apiClient, useAuthStore } from '@indipia/shared'
import type { NavFn } from '../pages/SchedulerPage'
import { useCommonCodes } from '../context/CommonCodesContext'
import { ErrorBanner } from '../components/AsyncState'
import { Button, Counter, Page, PageHeader } from '@indipia/ui-core'

type Props = { navigate: NavFn }

interface DurationSetting {
  workTypeCode: string
  duration: number
  customized?: boolean
}

export function DurationSettingsView({ navigate: _navigate }: Props) {
  const user = useAuthStore(s => s.user)
  const { codes } = useCommonCodes()
  const workTypes = codes?.workTypes ?? []

  const [durations, setDurations] = useState<Map<string, number>>(new Map())
  const [loading,   setLoading]   = useState(true)
  const [error,     setError]     = useState('')
  const [loadError, setLoadError] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)

  const initializedRef = useRef(false)
  const saveTimerRef    = useRef<ReturnType<typeof setTimeout> | null>(null)
  // 사용자가 값을 바꾼 공종(저장 대상) / 서버에 개인화 값이 있는 공종(초기화 대상)
  const dirtyRef        = useRef<Set<string>>(new Set())
  const customizedRef   = useRef<Set<string>>(new Set())

  const headers = {
    ...(user?.compId   ? { 'X-Comp-Id': String(user.compId)   } : {}),
    ...(user?.memberId ? { 'X-User-Id': String(user.memberId) } : {}),
  }

  // 표시값: 서버가 내려준 값(개인화 → 마스터 표준), 없으면 1
  const fetchDurations = async () => {
    const res = await apiClient.get<{ data: { workTypeDurations: DurationSetting[] } }>('/schedule/settings', { headers })
    const list = res.data?.data?.workTypeDurations ?? []
    const m = new Map<string, number>()
    const customized = new Set<string>()
    list.forEach(d => {
      m.set(d.workTypeCode, d.duration)
      if (d.customized) customized.add(d.workTypeCode)
    })
    customizedRef.current = customized
    setDurations(m)
  }

  useEffect(() => {
    setLoading(true)
    setLoadError(false)
    fetchDurations()
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey])

  const getDays = (code: string) => durations.get(code) ?? 1

  const setDays = (code: string, val: number) => {
    dirtyRef.current.add(code)
    setDurations(prev => {
      const m = new Map(prev)
      m.set(code, Math.max(1, Math.floor(val) || 1))
      return m
    })
  }

  // 값을 바꾼 공종만 저장 (바꾸지 않은 공종에 개인화 값이 생기지 않도록)
  const handleSave = async () => {
    const codesToSave = Array.from(dirtyRef.current)
    if (codesToSave.length === 0) return
    setError('')
    try {
      const workTypeDurations = codesToSave.map(code => ({
        workTypeCode: code,
        duration:     getDays(code),
      }))
      await apiClient.patch('/schedule/settings', { workTypeDurations }, { headers })
      codesToSave.forEach(code => { dirtyRef.current.delete(code); customizedRef.current.add(code) })
    } catch {
      setError('저장 중 오류가 발생했습니다.')
    }
  }

  // 초기화: 개인화 값을 삭제하고 마스터 표준 소요일로 복원
  const handleReset = async () => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    setError('')
    const targets = Array.from(new Set([...customizedRef.current, ...dirtyRef.current]))
    dirtyRef.current.clear()
    try {
      await Promise.all(targets.map(code =>
        apiClient.delete(`/schedule/settings/work-type-durations/${encodeURIComponent(code)}`, { headers })))
      await fetchDurations()
    } catch {
      setError('초기화 중 오류가 발생했습니다.')
    }
  }

  // 변경 시 자동 저장 (디바운스) — 최초 로드로 상태가 채워지는 시점은 저장에서 제외
  useEffect(() => {
    if (loading) return
    if (!initializedRef.current) { initializedRef.current = true; return }
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    saveTimerRef.current = setTimeout(() => { void handleSave() }, 600)
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, durations])

  const half  = Math.ceil(workTypes.length / 2)
  const leftCol  = workTypes.slice(0, half)
  const rightCol = workTypes.slice(half)

  const renderCol = (list: typeof workTypes) => (
    <div style={{ background: 'var(--shell-surface)', border: 'var(--border-width-hairline) solid var(--shell-border)', borderRadius: 16, overflow: 'hidden', flex: 1 }}>
      {list.map((w, i) => {
        const color = w.workTypeColor ?? 'var(--shell-on-surface-2)'
        const days  = getDays(w.workTypeCode)
        return (
          <div key={w.workTypeCode} style={{ display: 'flex', alignItems: 'center', padding: '13px 18px', gap: 14, borderTop: i > 0 ? '1px solid var(--shell-border)' : 'none' }}>
            <div style={{ width: 9, height: 9, borderRadius: '50%', background: color, flexShrink: 0 }} />
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--shell-on-surface)', flex: 1 }}>{w.workTypeName}</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
              <Counter value={days} onChange={v => setDays(w.workTypeCode, v)} min={1} size="sm" />
              <span style={{ fontSize: 'var(--type-caption-size)', fontWeight: 600, color: 'var(--shell-on-surface)' }}>일</span>
            </div>
          </div>
        )
      })}
    </div>
  )

  return (
    <div style={{ minHeight: '100%' }}>
      {loading ? (
        <div aria-busy="true" style={{ textAlign: 'center', padding: '60px 0', color: 'var(--shell-hint)', fontSize: 13 }}>불러오는 중...</div>
      ) : (
        <Page width="lg">
          <PageHeader title="공종별 기본 소요일" subtitle="공종별 기본 소요일을 설정합니다. 스케줄 생성 시 기본값으로 적용됩니다." />

          {loadError && (
            <div style={{ marginBottom: 16 }}>
              <ErrorBanner message="저장된 소요일 설정을 불러오지 못했습니다." onRetry={() => setReloadKey(k => k + 1)} />
            </div>
          )}

          {workTypes.length === 0 ? (
            <div aria-busy="true" style={{ textAlign: 'center', padding: '32px 0', color: 'var(--shell-hint)', fontSize: 12 }}>공종 정보를 불러오는 중...</div>
          ) : (
            <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
              {renderCol(leftCol)}
              {renderCol(rightCol)}
            </div>
          )}

          {/* 자동 저장 상태 */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10 }}>
            {error && <div className="field error" style={{ marginRight: 'auto' }} role="alert"><span className="help">{error}</span></div>}
            <Button variant="primary" size="lg" onClick={handleReset}>초기화</Button>
          </div>

        </Page>
      )}
    </div>
  )
}
